import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import {
  ChefHat, Sparkles, Clock, RefreshCw, AlertTriangle, Snowflake,
  Loader2, ThumbsUp, ThumbsDown, ListPlus, Plus, X as XIcon,
} from "lucide-react";
import { daysRemaining } from "@/lib/shelfLife";
import { toast } from "sonner";
import LittleDetour from "@/components/LittleDetour";

const DIFFICULTY_TONE = {
  easy: "bg-hearth-sage/15 text-hearth-sageDark",
  medium: "bg-hearth-amber/20 text-[#7A5320]",
  advanced: "bg-hearth-coral/15 text-hearth-coral",
};

const CUISINE_PRESETS = [
  "Italian", "Indian", "Chinese", "Thai", "Japanese", "Mexican",
  "Mediterranean", "Middle Eastern", "French", "Vietnamese", "Korean",
  "Greek", "Spanish", "American", "British", "Australian",
];

export default function UseSoon() {
  const [pantry, setPantry] = useState([]);
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cuisines (household-wide)
  const [cuisines, setCuisines] = useState([]);
  const [subRegions, setSubRegions] = useState({}); // { "Indian": ["Punjabi", "Gujarati"] }
  const [taxonomy, setTaxonomy] = useState({});     // { "Indian": ["Punjabi", ...] }
  const [cuisineInput, setCuisineInput] = useState("");
  const [savingCuisines, setSavingCuisines] = useState(false);
  const [dietType, setDietType] = useState("non_veg");  // household-wide dietary rule

  // Recipe feedback (household-wide, persisted)
  const [feedback, setFeedback] = useState({});   // {title: "like" | "dislike"}

  const loadPantry = useCallback(async () => {
    try {
      const { data } = await api.get("/grocery", { params: { status: "pantry" } });
      setPantry(data);
    } catch (e) { console.error(e); }
  }, []);

  const loadHousehold = useCallback(async () => {
    try {
      const { data } = await api.get("/household/me");
      setCuisines(data.cuisines || []);
      setSubRegions(data.cuisine_sub_regions || {});
      setDietType(data.diet_type || "non_veg");
    } catch (e) { console.error(e); }
  }, []);

  const loadTaxonomy = useCallback(async () => {
    try {
      const { data } = await api.get("/cuisines/taxonomy");
      setTaxonomy(data.taxonomy || {});
    } catch (e) { console.error(e); }
  }, []);

  const loadFeedback = useCallback(async () => {
    try {
      const { data } = await api.get("/recipes/feedback");
      const map = {};
      (data.feedback || []).forEach((r) => { map[r.recipe_title] = r.rating; });
      setFeedback(map);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadPantry(); loadHousehold(); loadFeedback(); loadTaxonomy(); }, [loadPantry, loadHousehold, loadFeedback, loadTaxonomy]);

  const expiring = useMemo(() => {
    return pantry
      .map((it) => ({ it, info: daysRemaining(it) }))
      .filter((x) => x.info && x.info.remaining <= 2)
      .sort((a, b) => a.info.remaining - b.info.remaining);
  }, [pantry]);

  const fetchRecipes = useCallback(async () => {
    if (expiring.length === 0) {
      toast.info("Nothing about to spoil — great pantry discipline.");
      return;
    }
    setLoading(true); setError(null);
    try {
      const items = expiring.map((x) => x.it.name);
      const { data } = await api.post("/recipes/use-soon", { items });
      setResp(data);
      if (data.error) setError(data.error);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [expiring]);

  useEffect(() => {
    if (expiring.length > 0 && !resp) fetchRecipes();
  }, [expiring.length, resp, fetchRecipes]);

  // -------- Cuisine preferences --------
  const persistCuisines = async (nextCuisines, nextSubs) => {
    setSavingCuisines(true);
    try {
      await api.put("/household/me", { cuisines: nextCuisines, cuisine_sub_regions: nextSubs });
      setCuisines(nextCuisines);
      setSubRegions(nextSubs);
      // New cuisines mean a different AI prompt & cache key — clear current recipes
      // so the user's next "New ideas" hits fresh generation.
      setResp(null);
      toast.success("Cuisine preferences saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingCuisines(false); }
  };

  const toggleCuisine = (c) => {
    const nextCuisines = cuisines.includes(c) ? cuisines.filter((x) => x !== c) : [...cuisines, c];
    // If we're removing a cuisine, drop its sub-regions too.
    const nextSubs = { ...subRegions };
    if (!nextCuisines.includes(c)) delete nextSubs[c];
    persistCuisines(nextCuisines, nextSubs);
  };

  const toggleSubRegion = (cuisine, region) => {
    const current = subRegions[cuisine] || [];
    const next = current.includes(region) ? current.filter((x) => x !== region) : [...current, region];
    const nextSubs = { ...subRegions };
    if (next.length === 0) delete nextSubs[cuisine]; else nextSubs[cuisine] = next;
    persistCuisines(cuisines, nextSubs);
  };

  const addCustomCuisine = () => {
    const c = cuisineInput.trim();
    if (!c) return;
    if (cuisines.some((x) => x.toLowerCase() === c.toLowerCase())) {
      setCuisineInput("");
      return;
    }
    persistCuisines([...cuisines, c], subRegions);
    setCuisineInput("");
  };

  const changeDietType = async (next) => {
    if (next === dietType) return;
    const prev = dietType;
    setDietType(next);
    setSavingCuisines(true);
    try {
      await api.put("/household/me", { diet_type: next });
      // diet_type flows into every recipe LLM prompt & cache key — bust
      // the current use-soon panel so the next fetch reflects the new rule.
      setResp(null);
      const label = next === "veg" ? "Vegetarian" : next === "vegan" ? "Vegan" : "No restriction";
      toast.success(`Diet set to ${label}`);
    } catch (e) {
      setDietType(prev);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSavingCuisines(false); }
  };

  // -------- Recipe feedback --------
  const rate = async (recipe, rating) => {
    // Optimistic update
    const prev = feedback[recipe.title];
    const next = prev === rating ? undefined : rating; // toggle off if same
    setFeedback((f) => {
      const copy = { ...f };
      if (next) copy[recipe.title] = next; else delete copy[recipe.title];
      return copy;
    });
    try {
      // If toggled off, we still send the current rating to persist "no opinion" as latest opinion.
      // Simpler: only POST when there IS a rating; a re-rate with the SAME value acts as toggle-off client-side but
      // will still upsert the same value in the DB. Good enough for the like/dislike UX.
      await api.post("/recipes/feedback", {
        recipe_title: recipe.title,
        rating: next || rating,
        uses: recipe.uses || [],
        cuisines,
        sub_regions: Object.values(subRegions).flat(),
      });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // -------- Add missing ingredients --------
  const addMissing = async (recipe) => {
    const ings = (recipe.ingredients && recipe.ingredients.length > 0)
      ? recipe.ingredients
      : (recipe.uses || []);
    if (!ings.length) {
      toast.info("No ingredient list on this recipe");
      return;
    }
    try {
      const { data } = await api.post("/recipes/add-missing", { ingredients: ings });
      if (data.count > 0) {
        toast.success(`Added ${data.count} missing ingredient${data.count === 1 ? "" : "s"} to grocery`);
      } else {
        toast.info("Everything's already on your grocery list");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Kitchen rescue"
        title="Too good to waste"
        subtitle="Recipes that turn what's about to spoil into a meal worth eating."
        action={
          <div className="flex items-center gap-2">
            <LittleDetour />
            <Button data-testid="use-soon-refresh" onClick={fetchRecipes} disabled={loading || expiring.length === 0}
                    className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white px-5 h-10">
              {loading ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <RefreshCw size={14} className="mr-1.5"/>}
              {resp ? "New ideas" : "Get recipes"}
            </Button>
          </div>
        }
      />

      {/* Cuisine preferences */}
      <section className="warm-card p-6 mb-6" data-testid="cuisine-preferences">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-display text-2xl text-hearth-ink">Cuisine preferences</h3>
          <span className="caption">the AI will lean this way</span>
        </div>

        {/* Household-wide dietary rule — a HARD constraint on every recipe */}
        <div className="mb-4" data-testid="diet-type-row">
          <div className="caption text-hearth-mute mb-2">How does your household eat?</div>
          <div className="inline-flex rounded-full bg-hearth-stone/60 p-1 gap-1" role="radiogroup" aria-label="Household dietary rule">
            {[
              { key: "non_veg", label: "No restriction", hint: "Meat, fish, dairy, eggs — all fair game." },
              { key: "veg",     label: "Vegetarian",     hint: "No meat or fish. Dairy + eggs OK." },
              { key: "vegan",   label: "Vegan",          hint: "No animal products at all." },
            ].map((opt) => {
              const active = dietType === opt.key;
              return (
                <button
                  key={opt.key}
                  data-testid={`diet-type-${opt.key}`}
                  role="radio"
                  aria-checked={active}
                  title={opt.hint}
                  disabled={savingCuisines}
                  onClick={() => changeDietType(opt.key)}
                  className={`px-4 py-1.5 text-sm rounded-full transition ${
                    active
                      ? "bg-hearth-clay text-white shadow-sm"
                      : "text-hearth-mute hover:text-hearth-ink"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-hearth-mute italic mt-2">
            {dietType === "non_veg" && "No restriction — recipes may include meat, poultry, fish, or seafood."}
            {dietType === "veg" && "Every recipe suggested is vegetarian — no meat, poultry, fish, or seafood."}
            {dietType === "vegan" && "Every recipe suggested is vegan — no animal products at all."}
          </p>
        </div>

        <p className="text-sm text-hearth-mute mb-3 italic">
          Pick what your household loves — recipes will skew toward those styles.
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {CUISINE_PRESETS.map((c) => {
            const active = cuisines.includes(c);
            return (
              <button
                key={c}
                data-testid={`cuisine-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                disabled={savingCuisines}
                onClick={() => toggleCuisine(c)}
                className={`chip transition ${
                  active
                    ? "bg-hearth-clay text-white"
                    : "bg-hearth-stone text-hearth-mute hover:text-hearth-ink"
                }`}
              >
                {c}
              </button>
            );
          })}
          {cuisines
            .filter((c) => !CUISINE_PRESETS.includes(c))
            .map((c) => (
              <button
                key={c}
                data-testid={`cuisine-chip-custom-${c.toLowerCase().replace(/\s+/g, "-")}`}
                disabled={savingCuisines}
                onClick={() => toggleCuisine(c)}
                className="chip bg-hearth-clay text-white inline-flex items-center gap-1"
              >
                {c} <XIcon size={11} />
              </button>
            ))}
        </div>

        {/* Sub-regions — one row per selected cuisine that has a taxonomy entry */}
        {cuisines.some((c) => (taxonomy[c] || []).length > 0) && (
          <div className="mt-1 mb-3 space-y-3 border-l-2 border-hearth-clay/25 pl-4" data-testid="cuisine-sub-regions">
            {cuisines.map((c) => {
              const regions = taxonomy[c] || [];
              if (regions.length === 0) return null;
              const selected = new Set(subRegions[c] || []);
              return (
                <div key={c} data-testid={`sub-region-row-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="caption text-hearth-mute mb-1.5">
                    <span className="text-hearth-clayDark font-medium">{c}</span> — regional flavours
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {regions.map((r) => {
                      const active = selected.has(r);
                      return (
                        <button
                          key={r}
                          data-testid={`sub-region-chip-${c.toLowerCase().replace(/\s+/g, "-")}-${r.toLowerCase().replace(/\s+/g, "-")}`}
                          disabled={savingCuisines}
                          onClick={() => toggleSubRegion(c, r)}
                          className={`chip text-xs transition ${
                            active
                              ? "bg-hearth-terracotta text-white"
                              : "bg-hearth-stone/60 text-hearth-mute hover:text-hearth-ink hover:bg-hearth-stone"
                          }`}
                        >
                          {r}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <p className="text-xs text-hearth-mute italic">
              Pick a region and we&apos;ll suggest dishes that actually belong there — Punjabi Chole instead of a generic chickpea curry.
            </p>
          </div>
        )}
        <div className="flex gap-2 items-center">
          <Input
            data-testid="cuisine-custom-input"
            value={cuisineInput}
            onChange={(e) => setCuisineInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomCuisine(); } }}
            placeholder="Add another cuisine…"
            className="input-warm h-10 max-w-xs"
          />
          <Button
            data-testid="cuisine-custom-add"
            onClick={addCustomCuisine}
            disabled={!cuisineInput.trim() || savingCuisines}
            variant="ghost"
            className="rounded-full border border-hearth-line hover:bg-hearth-stone h-10"
          >
            <Plus size={14} className="mr-1.5" /> Add
          </Button>
        </div>
      </section>

      {/* Ingredients strip */}
      <section className="warm-card p-6 mb-6">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-display text-2xl text-hearth-ink">Going bad soon</h3>
          <span className="caption">{expiring.length} item{expiring.length === 1 ? "" : "s"}</span>
        </div>
        {expiring.length === 0 ? (
          <p className="text-hearth-mute text-sm italic">Pantry is in great shape — nothing within 2 days of going bad.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {expiring.map(({ it, info }) => (
              <div key={it.id} className="chip bg-hearth-coral/10 text-hearth-coral inline-flex items-center gap-1.5">
                <span className="font-medium">{it.name}</span>
                <span className="font-mono opacity-70">
                  {info.remaining === 0 ? "today" : info.remaining === 1 ? "1d" : `${info.remaining}d`}
                </span>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-hearth-mute mt-3 italic">
          Note: cooking a recipe never removes items from your list — inventory updates stay manual.
        </p>
      </section>

      {/* Recipes */}
      {error && (
        <div className="warm-card p-5 mb-6 border-hearth-coral/30 bg-hearth-coral/5" data-testid="use-soon-error">
          <div className="flex items-start gap-2 text-hearth-coral">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium">
                {error.toLowerCase().includes("budget")
                  ? "Recipe AI is on a coffee break"
                  : "Couldn't fetch recipes right now"}
              </p>
              <p className="text-xs text-hearth-coral/80 mt-1">
                {error.toLowerCase().includes("budget")
                  ? "Your Emergent Universal Key budget is exhausted. Top it up from Profile → Universal Key → Add Balance, then click ‘New ideas’."
                  : "Try again in a moment — we'll pull fresh ideas from the kitchen."}
              </p>
            </div>
          </div>
        </div>
      )}

      {loading && !resp && (
        <div className="text-center py-16">
          <Loader2 size={28} className="mx-auto animate-spin text-hearth-clay" />
          <p className="text-hearth-mute mt-3 font-display italic text-lg">Whisking up ideas…</p>
        </div>
      )}

      {resp?.recipes?.length > 0 && (
        <>
          {resp.cached && (
            <p className="caption mb-3 inline-flex items-center gap-1.5 text-hearth-mute">
              <Snowflake size={11} /> showing today&apos;s cached suggestions
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {resp.recipes.map((r, idx) => {
              const rated = feedback[r.title];
              return (
              <article key={`${r.title}-${idx}`} data-testid={`recipe-card-${idx}`}
                       className="warm-card p-6 fade-up flex flex-col">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-hearth-clay/12 text-hearth-clayDark grid place-items-center shrink-0">
                    <ChefHat size={18} strokeWidth={1.6} />
                  </div>
                  <span className={`chip ${DIFFICULTY_TONE[r.difficulty] || DIFFICULTY_TONE.easy}`}>
                    {r.difficulty || "easy"}
                  </span>
                </div>
                <h3 className="font-display text-2xl text-hearth-ink leading-tight">{r.title}</h3>
                {r.time_min != null && (
                  <p className="caption mt-1 inline-flex items-center gap-1.5"><Clock size={11}/> ~{r.time_min} min</p>
                )}
                {r.blurb && <p className="text-sm text-hearth-mute mt-3 italic leading-relaxed">{r.blurb}</p>}
                {r.uses?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {r.uses.map((u) => (
                      <span key={u} className="chip bg-hearth-sage/12 text-hearth-sageDark inline-flex items-center gap-1">
                        <Sparkles size={10} /> {u}
                      </span>
                    ))}
                  </div>
                )}
                {r.steps?.length > 0 && (
                  <ol className="mt-4 space-y-2 text-sm">
                    {r.steps.map((step, sidx) => (
                      <li key={`${idx}-${sidx}`} className="flex gap-2">
                        <span className="w-5 h-5 rounded-full bg-hearth-stone text-hearth-ink text-xs font-mono grid place-items-center shrink-0 mt-0.5">
                          {sidx + 1}
                        </span>
                        <span className="text-hearth-ink leading-relaxed">{step}</span>
                      </li>
                    ))}
                  </ol>
                )}

                {/* Actions: rating + add missing */}
                <div className="mt-5 pt-4 border-t border-hearth-line/60 flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <button
                      data-testid={`recipe-like-${idx}`}
                      onClick={() => rate(r, "like")}
                      title="Like this recipe"
                      className={`p-2 rounded-full transition ${
                        rated === "like"
                          ? "bg-hearth-sage/25 text-hearth-sageDark"
                          : "text-hearth-mute hover:bg-hearth-sage/15 hover:text-hearth-sageDark"
                      }`}
                    >
                      <ThumbsUp size={15} />
                    </button>
                    <button
                      data-testid={`recipe-dislike-${idx}`}
                      onClick={() => rate(r, "dislike")}
                      title="Not for us"
                      className={`p-2 rounded-full transition ${
                        rated === "dislike"
                          ? "bg-hearth-coral/20 text-hearth-coral"
                          : "text-hearth-mute hover:bg-hearth-coral/10 hover:text-hearth-coral"
                      }`}
                    >
                      <ThumbsDown size={15} />
                    </button>
                  </div>
                  <Button
                    data-testid={`recipe-add-missing-${idx}`}
                    onClick={() => addMissing(r)}
                    variant="ghost"
                    className="rounded-full border border-hearth-line hover:bg-hearth-stone h-9 text-xs"
                  >
                    <ListPlus size={13} className="mr-1.5" />
                    Add missing to grocery
                  </Button>
                </div>
              </article>
            );})}
          </div>
        </>
      )}

      {!loading && !resp && expiring.length === 0 && (
        <EmptyState icon={ChefHat} title="Nothing to rescue yet."
          message="Move items from your grocery list into the pantry, and when something gets close to going bad it'll show up here with recipe ideas." />
      )}
    </div>
  );
}
